
import ReactMarkdown from "react-markdown";
import { useState, useEffect } from "react";

interface YoutubeRendererProps {
  content: string;
}

/**
 * Special renderer for YouTube recommendation system article
 * with enhanced styling and error handling
 */
const YoutubeRenderer = ({ content }: YoutubeRendererProps) => {
  const [isContentLoaded, setIsContentLoaded] = useState(false);

  useEffect(() => {
    // Verify content is loaded properly
    if (content && content.length > 100) {
      console.log("YoutubeRenderer: Content loaded successfully");
      setIsContentLoaded(true);
    } else {
      console.warn("YoutubeRenderer: Content appears incomplete or missing");
    }
  }, [content]);

  // If content is very short or empty, show a friendly error
  if (!content || content.length < 100) {
    return (
      <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
        <h2 className="text-2xl font-semibold mb-3">Article Content</h2>
        <p className="mb-4">We're currently experiencing technical difficulties loading this article. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="youtube-recommendation prose prose-lg max-w-none">
      <style>
        {`
        .youtube-recommendation h1 {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #0f172a;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 0.5rem;
        }
        
        .youtube-recommendation h2 {
          font-size: 1.75rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: #1e293b;
        }
        
        .youtube-recommendation h3 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #334155;
        }
        
        .youtube-recommendation p {
          margin-bottom: 1rem;
          line-height: 1.7;
        }
        
        .youtube-recommendation ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .youtube-recommendation ul li {
          margin-bottom: 0.5rem;
        }
        
        .youtube-recommendation blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1rem;
          font-style: italic;
          color: #64748b;
          margin-bottom: 1rem;
        }
        
        .youtube-recommendation hr {
          margin: 2rem 0;
          border: 0;
          height: 1px;
          background-color: #e2e8f0;
        }
        
        .youtube-recommendation code {
          background-color: #f1f5f9;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.9em;
        }
        
        .youtube-recommendation pre {
          background-color: #f8fafc;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1.5rem;
          border: 1px solid #e2e8f0;
        }
        
        .youtube-recommendation pre code {
          background-color: transparent;
          padding: 0;
        }
        
        .youtube-recommendation strong {
          font-weight: 600;
          color: #334155;
        }
        `}
      </style>
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
};

export default YoutubeRenderer;
